import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTenant } from '../../contexts/TenantContext'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import { supabase } from '../../lib/supabase'
import { MainLayout } from '../../components/layouts/MainLayout'
import { Loading } from '../../components/common/Loading'
import { ErrorMessage } from '../../components/common/ErrorMessage'
import { Building2, Users, BookOpen, DollarSign, Activity } from 'lucide-react'

interface PlatformMetrics {
  totalTenants: number
  totalCandidates: number
  totalCourses: number
  totalRevenue: number
}

export function PlatformDashboardPage() {
  useDocumentTitle('プラットフォーム管理')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isPlatformAdmin } = useTenant()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<PlatformMetrics | null>(null)

  useEffect(() => {
    // プラットフォーム管理者権限チェック
    if (!isPlatformAdmin) {
      navigate('/mypage')
      return
    }

    if (user) {
      fetchDashboardData()
    }
  }, [user, isPlatformAdmin])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)

      // テナント数
      const { count: totalTenants } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')

      // 総受講者数（全entitlementsから重複排除）
      const { data: entitlementsData } = await supabase
        .from('entitlements')
        .select('user_id')
        .eq('status', 'active')

      const uniqueCandidates = new Set(entitlementsData?.map((e) => e.user_id) || [])
      const totalCandidates = uniqueCandidates.size

      // 総講座数
      const { count: totalCourses } = await supabase
        .from('courses')
        .select('*', { count: 'exact', head: true })

      // 総売上（全完了済みorders）
      const { data: allOrders } = await supabase
        .from('orders')
        .select('amount')
        .eq('status', 'completed')

      const totalRevenue = allOrders?.reduce((sum, order) => sum + order.amount, 0) || 0

      setMetrics({
        totalTenants: totalTenants || 0,
        totalCandidates,
        totalCourses: totalCourses || 0,
        totalRevenue,
      })
    } catch (err: any) {
      console.error('Error fetching platform dashboard data:', err)
      setError(err.message || 'データの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <MainLayout>
        <Loading message="ダッシュボードを読み込み中..." />
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            プラットフォーム管理ダッシュボード
          </h1>
          <p className="mt-2 text-gray-600">認定資格.com 全体の統計情報</p>
        </div>

        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={fetchDashboardData} />
          </div>
        )}

        {/* メトリクス */}
        {metrics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">テナント数</h3>
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalTenants}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">総受講者数</h3>
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalCandidates}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">総講座数</h3>
                <BookOpen className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalCourses}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">総売上</h3>
                <DollarSign className="h-5 w-5 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">
                ¥{metrics.totalRevenue.toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* システムヘルス */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="h-6 w-6 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">システムヘルス</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">データベース</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded">正常</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">API</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded">正常</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">ストレージ</span>
              <span className="px-2 py-1 bg-green-100 text-green-800 text-sm rounded">正常</span>
            </div>
          </div>
        </div>

        {/* クイックアクション */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/tenants')}
            className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg transition text-left"
          >
            <Building2 className="h-6 w-6 text-blue-600 mb-2" />
            <h3 className="font-medium text-gray-900">テナント管理</h3>
            <p className="text-sm text-gray-600">団体の作成・管理</p>
          </button>

          <button
            onClick={() => navigate('/admin/candidates')}
            className="p-4 bg-green-50 hover:bg-green-100 rounded-lg transition text-left"
          >
            <Users className="h-6 w-6 text-green-600 mb-2" />
            <h3 className="font-medium text-gray-900">全受講者管理</h3>
            <p className="text-sm text-gray-600">全受講者の名簿</p>
          </button>

          <button
            onClick={() => navigate('/admin/payments')}
            className="p-4 bg-yellow-50 hover:bg-yellow-100 rounded-lg transition text-left"
          >
            <DollarSign className="h-6 w-6 text-yellow-600 mb-2" />
            <h3 className="font-medium text-gray-900">決済管理</h3>
            <p className="text-sm text-gray-600">全決済の一覧</p>
          </button>
        </div>
      </div>
    </MainLayout>
  )
}
